import React, {useLayoutEffect, useRef} from "react";
import {componentInit, useObserver, useRerender} from "./CommonReact";
import {classNameScrollbar, remPixels, tooltipIfNeeded} from "./CommonHtml";
import {funcOrValOr, isInRange} from "./CommonJS";

// cols: [{ h?: header, f?: field, d?(index, iCol, col, rowData, cellData): display func, className?, classNameHeader?, props?, noSort?: header not clickable }]

export function UIVirtualTable({rows, styleHeight = undefined, rowHeight = 1.8, colGap = 0.25, cols = undefined, headerRenderer = undefined, colHeaderRenderer = undefined, colGrow = undefined, rowRenderer = undefined, oInterface = undefined,
                                   onRowClick = undefined, onRowDoubleClick = undefined, onClickCell = undefined, onMouseEnterCell = undefined, onSizing = undefined, onClickHeader = undefined, overscanRowCount = 4, scrollToIndex = undefined,
                                   className = undefined, classNameGrid = undefined, classNameRow = undefined, classNameCol = undefined, propsRow = undefined, dataTest = undefined, emptyVal = undefined}) {
    const rerender = useRerender();
    const vals = useRef({startIndex: 0, stopIndex: 0, hTm: {}}).current;
    const rTbl = useRef();
    Object.assign(vals, {rows});

    function calcSizes(cmd, scrollToIndex) {
        if (!rTbl.current) return;          // onScroll can evidently be called even after ref gets cleared.
        const pxRow = rowHeight * remPixels();
        const gridRect = rTbl.current.getBoundingClientRect();
        const fRowsFit = gridRect.height / pxRow;
        let fIdxWinTop = rTbl.current.scrollTop / pxRow, newTop;

        if (scrollToIndex < fIdxWinTop)
            fIdxWinTop = newTop = scrollToIndex;
        else if (scrollToIndex >= fIdxWinTop + (fRowsFit | 0))
            fIdxWinTop = newTop = scrollToIndex - Math.max(fRowsFit - 1 | 0, 0);

        if (newTop >= 0)
            rTbl.current.scrollTop = newTop * pxRow;
        // const setScroll = () => rTbl.current.scrollTop = Math.ceil(fWinTopIndex) * pxRow;
        // !rTbl.current.scrollTop? setScroll() : vals.hTm.scTop = setTimeout(setScroll, 100);         // scrollTop 0 is not best indication, but catch if just presented and need to jump ahead - do it immediately.

        vals.startIndex = Math.max(fIdxWinTop - overscanRowCount/2 |0, 0);     // have half the overscan rows before visible view
        vals.stopIndex = Math.ceil(fIdxWinTop + fRowsFit + overscanRowCount / 2);

        vals.sclBarWidth = rTbl.current.offsetWidth - rTbl.current.clientWidth;
        // console.log("calcSizes", {cmd, scrollToIndex, scrollTop: rTbl.current.scrollTop, startIndex: vals.startIndex});
        //dbg && console.log("calcSizes ", {cmd, scrollToIndex, start: vals.startIndex, stop: vals.stopIndex, fRowsFit: (fRowsFit*10|0)/10, scrlBarWidth: vals.sclBarWidth, pxHeight: (gridRect.height*10|0)/10});
        if (!(vals.msLastScrollRecalc + 1000 > Date.now()) &&
            (vals.rows?.length >= fRowsFit) !== !!vals.sclBarWidth) {     // if bar needed but not there - recalc after bar appears...
            vals.msLastScrollRecalc = Date.now();                         // avoid re-layout loops
            vals.hTm.recalc = setTimeout(calcSizes);
        }

        const doOnSizing = () => onSizing?.({gridRect, startIndex: vals.startIndex, stopIndex: vals.stopIndex, fRowsFit, overscanRowCount, elGrid: rTbl.current});

        if (cmd !== "inRender") {
            doOnSizing();
            rerender();
        } else if (onSizing)
            vals.hTm.sz = setTimeout(doOnSizing);
    }

    useObserver(ResizeObserver, rTbl, "height", () => calcSizes("ResizeObserver"), {debounceMs: 100});

    useLayoutEffect(() => {
        if (!rTbl.current) return;
        if (rTbl.current.scrollHeight)
            calcSizes("layoutEffect");
        if (oInterface) Object.assign(oInterface, {
            getVisibleRowElements: () => ({elements: [...rTbl.current.children[0].children[0].children], index: vals.startIndex})
        });
        return () => Object.values(vals.hTm).forEach(clearTimeout);
    }, [rTbl.current, rows]);

    if (rTbl.current && isInRange(scrollToIndex, 0, rows?.length -1) && componentInit(vals, [scrollToIndex], "_initScroll"))
        calcSizes("inRender", scrollToIndex);

    const style = {height: rowHeight +"rem", width: "100%"};
    const rowStyle = {display: "flex", whiteSpace: "nowrap", overflow: "hidden", gap: colGap+"rem", alignItems: "center"};
    const colStyles = cols?.map((c, iCol) => ({
        flex: c.flex ?? `${c.wConst? 0 : colGrow?.[iCol] ?? c.w ?? 1} 0 ${c.wConst? c.wConst+"rem" : "0"}`,
        minWidth: 0,
        ...c.style
    }));

    if (!rowRenderer) rowRenderer = ({style, ...passParams}) => (
        <div key={passParams.index} className={funcOrValOr(classNameRow, passParams, "")}
             style={{...style, ...rowStyle}}
             onClick={onRowClick && (event => onRowClick({event, ...passParams}))}
             onDoubleClick={onRowDoubleClick && (event => onRowDoubleClick({event, ...passParams}))}
             {...propsRow}>
            {cols.map((col, iCol) => {
                const cellData = passParams.rowData[col.f];
                const cellParams = {...passParams, iCol, col, cellData};
                return (
                    <div key={iCol}
                         style={colStyles[iCol]}
                         className={`${funcOrValOr(classNameCol, cellParams, "")} ${col.className}`}
                         onMouseEnter={col.onMouseEnter || onMouseEnterCell?
                             (event => (col.onMouseEnter || onMouseEnterCell)({event, ...cellParams})) : tooltipIfNeeded}
                         onClick={!onClickCell? undefined : event => onClickCell({event, ...cellParams})}
                         {...col.props}>
                        {col.d? col.d(cellParams) : cellData}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div {...styleHeight && {style: {height: `${styleHeight}px`}}}
             className={`flex flex-col w-full ${!className?.match(/(^| )h-/) && "h-full"} ${className}`}>
            <div className="flex items-center">
                {cols && (
                    headerRenderer?
                        headerRenderer({cols, style, rowStyle, colStyles})
                        : <div style={{flexShrink: 0, ...style, ...rowStyle}}>
                            {cols.map((col, iCol) =>
                                <div key={iCol} style={{...colStyles[iCol], ...onClickHeader && !col.noSort && {cursor: "pointer"}}} className={col.classNameHeader}
                                     onClick={onClickHeader && !col.noSort && (event => onClickHeader({event, iCol, col}))}>
                                    {colHeaderRenderer? React.createElement(colHeaderRenderer, {iCol, col}) : col.h}
                                </div>)
                            }
                        </div>
                )}
                {!!vals.sclBarWidth && <div style={{width: `${vals.sclBarWidth}px`, flexShrink: 0}}/>}
            </div>
            <div data-test={`${dataTest}-ui-virtual-table`} ref={rTbl} className={classNameGrid ?? classNameScrollbar}
                 onScroll={e => calcSizes("onScroll")}
                 style={{flexGrow: 1, width: "100%", position: "relative", overflow: "hidden auto"}}>
                {//rTbl.current &&
                    <div style={{height: `${rows?.length * rowHeight}rem`}}>
                        <div style={{position: "absolute", top: `${vals.startIndex * rowHeight}rem`, width: "100%"}}>
                            {rows?.length? [...rows.slice(vals.startIndex, vals.stopIndex)]         // convert sparse cells into undefined cells, so map will iterate all items.
                                    .map((rowData, index) => rowData === undefined?
                                        <div key={index} style={style}/>
                                        :
                                        rowRenderer({style, rowData, index: vals.startIndex + index}))
                                :
                                funcOrValOr(emptyVal)
                            }
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}
